import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { MessageSquare, MapPin } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import { useUpdateFirestoreItem } from '../../hooks';
import { COLLECTIONS } from '../../constants';
import { getFormattedLocations } from '../../utils';
import { useAuth } from '../../providers/AuthProvider';
import { ProfileFormData } from './UserProfile';
import 'react-phone-input-2/lib/style.css';
import { Select } from '../../components/Select';

// Types
interface SettingsTabProps {
  userData: any;
  isPending: boolean;
}

// Utility Components
const FormField = ({
  defaultMessage,
  error,
  children,
  className = '',
}: {
  defaultMessage: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={className}>
    <label className='block text-sm font-medium text-gray-700 mb-1'>
      {defaultMessage}
      {children}
    </label>
    {error && (
      <p className='mt-1 text-sm text-red-600 flex items-center'>{error}</p>
    )}
  </div>
);

const SettingsSkeleton = () => (
  <div className='bg-white rounded-lg shadow-sm p-6'>
    <div className='h-8 bg-gray-200 rounded w-48 mb-6 animate-pulse' />
    <div className='space-y-6'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {[1, 2, 3, 4, 5].map((field) => (
          <div key={field} className='space-y-2'>
            <div className='h-5 bg-gray-200 rounded w-24 animate-pulse' />
            <div className='h-10 bg-gray-200 rounded w-full animate-pulse' />
          </div>
        ))}
        <div className='md:col-span-2 space-y-2'>
          <div className='h-5 bg-gray-200 rounded w-24 animate-pulse' />
          <div className='h-32 bg-gray-200 rounded w-full animate-pulse' />
        </div>
      </div>
      <div className='flex justify-end space-x-4'>
        <div className='h-10 bg-gray-200 rounded w-24 animate-pulse' />
        <div className='h-10 bg-gray-200 rounded w-40 animate-pulse' />
      </div>
    </div>
  </div>
);

// Main component
export const SettingsTab: React.FC<SettingsTabProps> = ({
  userData,
  isPending,
}) => {
  const { formatMessage } = useIntl();
  const { user } = useAuth();

  // Mutations
  const { mutate: updateUserData, isPending: isPendingUpdateUserData } =
    useUpdateFirestoreItem();

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid },
    reset,
    setValue,
    watch,
    control,
  } = useForm<ProfileFormData>({
    defaultValues: {
      displayName: '',
      email: '',
      phoneNumber: '',
      telegram: '',
      location: '',
      bio: '',
    },
    mode: 'all',
  });

  // Initialize form when userData changes
  useEffect(() => {
    if (userData) {
      reset({
        displayName: userData.displayName || '',
        email: userData.email || '',
        phoneNumber: userData.phoneNumber || '',
        telegram: userData.telegram || '',
        location: userData.location || '',
        bio: userData.bio || '',
      });
    }
  }, [userData, reset]);

  const onSubmit = (data: ProfileFormData) => {
    updateUserData({
      collection: COLLECTIONS.users,
      id: user?.uid,
      newData: data,
    });
  };

  const bioFormValue = watch('bio');

  const locations = getFormattedLocations(formatMessage);

  // Validation regex patterns
  const namePattern = /^[a-zA-Zа-яА-ЯёЁ\s'-]+$/; // Only letters, spaces, apostrophes, hyphens
  const bioPattern = /^[a-zA-Zа-яА-ЯёЁ0-9\s.,!?()'";\-:]+$/; // Letters, numbers, basic punctuation

  if (isPending || isPendingUpdateUserData) {
    return <SettingsSkeleton />;
  }

  return (
    <div className='bg-white rounded-lg shadow-sm p-6'>
      <h2 className='text-xl font-semibold mb-6'>
        <FormattedMessage id='profileData' defaultMessage='Данные профиля' />
      </h2>
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <FormField
            defaultMessage={formatMessage({
              id: 'name',
              defaultMessage: 'Имя',
            })}
            error={errors.displayName?.message}
          >
            <input
              type='text'
              {...register('displayName', {
                required: formatMessage({
                  id: 'contact.nameRequired',
                  defaultMessage: 'Имя обязательно',
                }),
                minLength: {
                  value: 2,
                  message: formatMessage({
                    id: 'auth.nameMinLength',
                    defaultMessage: 'Имя должно содержать минимум 2 символа',
                  }),
                },
                pattern: {
                  value: namePattern,
                  message: formatMessage({
                    id: 'validation.noSpecialChars',
                    defaultMessage:
                      'Имя может содержать только буквы, апострофы, дефисы и пробелы',
                  }),
                },
              })}
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                errors.displayName ? 'border-red-500' : 'border-gray-300'
              }`}
              onKeyDown={(e) => {
                // Prevent typing special characters
                const char = e.key;
                if (
                  char.length === 1 &&
                  !namePattern.test(char) &&
                  char !== 'Backspace'
                ) {
                  e.preventDefault();
                }
              }}
            />
          </FormField>

          <FormField
            defaultMessage={formatMessage({
              id: 'email',
              defaultMessage: 'Email',
            })}
            error={errors.email?.message}
          >
            <input
              type='email'
              {...register('email', {
                required: formatMessage({
                  id: 'contact.emailRequired',
                  defaultMessage: 'Электронная почта обязательна',
                }),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: formatMessage({
                    id: 'contact.emailPattern',
                    defaultMessage: 'Неверный формат email',
                  }),
                },
              })}
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </FormField>

          <FormField
            defaultMessage={formatMessage({
              id: 'phone',
              defaultMessage: 'Телефон',
            })}
            error={errors.phoneNumber?.message}
          >
            <PhoneInput
              country='ro'
              value={userData?.phoneNumber}
              onChange={(phone) =>
                setValue('phoneNumber', phone, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              inputProps={{
                className: `w-full pl-12 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none ${
                  errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                }`,
              }}
              onlyCountries={['ro', 'md', 'ru', 'ua']}
            />
          </FormField>
          <FormField
            defaultMessage={formatMessage({
              id: 'telegram',
              defaultMessage: 'Telegram',
            })}
            error={errors.telegram?.message}
          >
            <div className='relative'>
              <MessageSquare className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
              <input
                type='text'
                {...register('telegram', {
                  validate: (value) =>
                    !value ||
                    /^@[a-zA-Z0-9_]{4,31}(?<!__)$/.test(value) ||
                    formatMessage({
                      id: 'addService.telegramFormat',
                      defaultMessage: 'Неверный формат username Telegram',
                    }),
                })}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.telegram ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder='@username'
                onKeyDown={(e) => {
                  if (
                    e.key !== 'Backspace' &&
                    e.key !== 'Delete' &&
                    e.key !== 'ArrowLeft' &&
                    e.key !== 'ArrowRight' &&
                    e.key !== 'Tab'
                  ) {
                    const char = e.key;
                    const isValid = /^[a-zA-Z0-9_@]$/.test(char);
                    if (!isValid) {
                      e.preventDefault();
                    }
                  }
                }}
              />
            </div>
          </FormField>

          <FormField
            defaultMessage={formatMessage({
              id: 'city',
              defaultMessage: 'Город',
            })}
            error={errors.location?.message}
          >
            <div className='relative w-full'>
              <MapPin className='absolute z-10 left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
              <Controller
                name='location'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    field={field}
                    options={locations}
                    placeholder={formatMessage({
                      id: 'addService.locationPlaceholder',
                      defaultMessage: 'Укажите город',
                    })}
                    errors={errors}
                    name={field.name}
                  />
                )}
              />
              {errors.location && (
                <p className='mt-1 text-sm text-red-500'>
                  {formatMessage({
                    id: 'addService.locationError',
                    defaultMessage: 'Пожалуйста, выберите город',
                  })}
                </p>
              )}
            </div>
          </FormField>

          <FormField
            defaultMessage={formatMessage({
              id: 'aboutMe',
              defaultMessage: 'О себе',
            })}
            error={errors.bio?.message}
            className='md:col-span-2'
          >
            <textarea
              {...register('bio', {
                pattern: {
                  value: bioPattern,
                  message: formatMessage({
                    id: 'validation.noSpecialCharsInBio',
                    defaultMessage:
                      'Текст может содержать только буквы, цифры и основные знаки препинания',
                  }),
                },
                maxLength: {
                  value: 500,
                  message: formatMessage({
                    id: 'validation.bioMaxLength',
                    defaultMessage: 'Текст не должен превышать 500 символов',
                  }),
                },
              })}
              rows={4}
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                errors.bio ? 'border-red-500' : 'border-gray-300'
              }`}
              onKeyDown={(e) => {
                if (
                  e.key !== 'Backspace' &&
                  e.key !== 'Delete' &&
                  e.key !== 'ArrowLeft' &&
                  e.key !== 'ArrowRight' &&
                  e.key !== 'Tab' &&
                  e.key !== 'Enter'
                ) {
                  const char = e.key;
                  const isValid = bioPattern.test(char);
                  if (!isValid && char.length === 1) {
                    e.preventDefault();
                  }
                }
              }}
              placeholder={formatMessage({
                id: 'addService.bioPlaceholder',
                defaultMessage: 'Расскажите немного о себе...',
              })}
            />
            <div className='text-xs text-gray-500 mt-1'>
              <FormattedMessage
                id='validation.charsRemaining'
                defaultMessage='Осталось символов: {count}'
                values={{ count: 500 - (bioFormValue?.length || 0) }}
              />
            </div>
          </FormField>
        </div>

        <div className='flex justify-end gap-4'>
          <button
            type='button'
            onClick={() => reset()}
            className='px-6 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50'
          >
            <FormattedMessage id='cancel' defaultMessage='Отмена' />
          </button>
          <button
            type='submit'
            disabled={
              !isValid || isPendingUpdateUserData || isSubmitting || !isDirty
            }
            className={`px-6 py-2 rounded-lg ${
              isSubmitting || !isDirty || !isValid
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <FormattedMessage
              id='saveChanges'
              defaultMessage='Сохранить изменения'
            />
          </button>
        </div>
      </form>
    </div>
  );
};
